import * as React from 'react';
import { Link, useLocation } from 'react-router';
import { cn } from '@/lib/utils';
import { useTenantPath } from '@/lib/auth/TenantCodeContext';

export interface NavLinkProps {
  href: string;
  icon: string | React.ReactNode;
  label: string;
  showLabel?: boolean;
  showIndicator?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  className?: string;
}

export function NavLink({
  href,
  icon,
  label,
  showLabel = false,
  showIndicator = false,
  onClick,
  className,
}: NavLinkProps) {
  const location = useLocation();
  const resolvedHref = useTenantPath(href);

  const isActive =
    location.pathname === resolvedHref ||
    location.pathname.startsWith(resolvedHref + '/');

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (onClick && !e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      onClick(e);
    }
  };

  const iconColorClass = cn(
    'transition-colors duration-150',
    isActive
      ? 'text-(--sidebar-link-icon-active)'
      : 'text-(--sidebar-link-icon-default) group-hover:text-(--sidebar-link-icon-hover)'
  );

  const renderIcon = () => {
    if (typeof icon === 'string') {
      return (
        <i
          className={cn(
            icon,
            '[font-size:var(--sidebar-icon-size)]',
            iconColorClass
          )}
          aria-hidden="true"
        />
      );
    }

    if (React.isValidElement(icon)) {
      return React.cloneElement(icon as React.ReactElement<{ className?: string; style?: React.CSSProperties }>, {
        className: cn(iconColorClass, (icon as React.ReactElement<{ className?: string }>).props.className),
        style: {
          width: 'var(--sidebar-icon-size)',
          height: 'var(--sidebar-icon-size)',
          ...((icon as React.ReactElement<{ style?: React.CSSProperties }>).props.style || {}),
        },
      });
    }

    return null;
  };

  return (
    <Link
      to={resolvedHref}
      aria-label={label}
      title={!showLabel ? label : undefined}
      onClick={handleClick}
      className={cn(
        'group flex items-center justify-start',
        'h-(--sidebar-link-height)',
        showLabel ? 'w-full' : 'w-(--sidebar-link-height)',
        'px-2.5 gap-3 translate-x-1',
        'rounded-(--sidebar-link-radius)',
        'transition-all duration-200 ease-in-out',
        isActive
          ? 'bg-(--sidebar-link-bg-active)'
          : 'bg-(--sidebar-link-bg-default) hover:bg-(--sidebar-link-bg-hover)',
        className
      )}
    >
      <span className="relative shrink-0 flex items-center justify-center">
        {renderIcon()}
        {showIndicator && (
          <span
            className="absolute -top-0.5 -right-0.5 size-2 bg-red-500 rounded-full border-2 border-bg-secondary"
            aria-label="New notifications"
          />
        )}
      </span>
      <span
        className={cn(
          'body-md-medium whitespace-nowrap overflow-hidden',
          'transition-all duration-200 ease-in-out',
          showLabel
            ? 'opacity-100 max-w-[200px]'
            : 'opacity-0 max-w-0',
          isActive
            ? 'text-white'
            : 'text-text-secondary group-hover:text-text-primary'
        )}
        aria-hidden={!showLabel}
      >
        {label}
      </span>
    </Link>
  );
}
